import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  FormControl,
} from '@angular/forms';
import { SelectionModel } from '@angular/cdk/collections';

import { MAT_DATE_LOCALE } from '@angular/material/core';
import { ja } from 'date-fns/locale';
import { provideDateFnsAdapter } from '@angular/material-date-fns-adapter';
import {
  MatDatepickerModule,
  MatDateRangePicker,
} from '@angular/material/datepicker';

import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { addYears, subDays, isBefore } from 'date-fns';
import { TenantViewModel } from '../device-list.component';
import { DeviceTypeViewModel } from '@components/device-type-list/device-type-list-table/device-type-list-table.component';

import { stringEquals } from '@utils/string.util';
import { FilterService } from '@services/filter.service';
import { DirectivesModule } from 'src/app/directive/auto-complete-off/auto-complete-off.module';
import { DatetimeRangePickerComponent } from '@components/shared/datetime-range-picker/datetime-range-picker.component';

/** デバイスのViewModel */
export type DeviceViewModel = {
  /** シリアルナンバー */
  serialNo: string;
  /** デバイス種類 */
  deviceTypeId: string;
  /** デバイス種類表示名 */
  deviceTypeDisplayName: string;
  /** 最終通信日時 */
  lastCommunicationAt: Date | null;
  /** テナント名 */
  tenantName: string;
  /**LPN ID */
  lpnId: string;
  /** 契約期限 */
  expiredAt: Date;
  /** デバイス名 */
  deviceName: string;
  /** テナントID */
  tenantId: string;
  /** 契約情報 */
  contractType: string | null;
  /** インポートファイル */
  file?: File;
  /** 契約開始日 */
  contractStartedAt: Date;
  /** 契約終了日 */
  contractExpiredAt: Date;
};

/** ダイアログオプション */
export type DialogOption = {
  tenantList?: TenantViewModel[];
  target?: DeviceViewModel;
};

interface DeviceListFilter {
  serialNo?: string;
  deviceName?: string;
  deviceTypeDisplayName?: string;
  lastCommunicationAt?: string;
  tenantName?: string;
  tenantId?: string;
  contractStartedAt?: string;
  expiredAt?: string;
  contractType?: string;
  sortState?: Sort;
  pageIndex?: number;
}

const pageId = 'DeviceList';

/** 基準の期間 */
const MAX_PERIOD = 7;

@Component({
  selector: 'app-device-list-table',
  standalone: true,
  providers: [
    {
      provide: MAT_DATE_LOCALE,
      useValue: ja,
    },
    provideDateFnsAdapter({
      parse: {
        dateInput: 'yyyy/MM/dd',
      },
      display: {
        dateInput: 'yyyy/MM/dd',
        monthYearLabel: 'yyyy/MM',
        dateA11yLabel: 'yyyy/MM/dd',
        monthYearA11yLabel: 'yyyy/MM',
      },
    }),
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatCheckboxModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    DatePipe,
    DirectivesModule,
    DatetimeRangePickerComponent,
    MatDatepickerModule,
    MatDateRangePicker,
  ],
  templateUrl: './device-list-table.component.html',
  styleUrl: './device-list-table.component.scss',
})
export class DeviceListTableComponent
  implements OnInit, AfterViewInit, OnChanges
{
  /** デバイス一覧 */
  @Input({ required: true }) deviceList: DeviceViewModel[] = [];

  /** テナント一覧 */
  @Input() tenantList: TenantViewModel[] = [];

  /** デバイス種類一覧 */
  @Input() deviceTypeList: DeviceTypeViewModel[] = [];

  /** フィルタ候補のデバイス種類表示名 */
  @Input() deviceTypeDisplayNames: string[] = [];

  /** フィルター用デバイス種類ID一覧 */
  @Input() filterDeviceTypeIdList: string[] = [];

  /** 現在のテナント */
  @Input() currentTenant: string = '';

  /** 閲覧のみか */
  @Input() readonly = false;

  /**ディスプレイ表示 */
  @Input() displayFlag: boolean = false;

  /** 登録確認を通知 */
  @Output() confirmed = new EventEmitter<void>();

  /** search */
  @Output() search = new EventEmitter<void>();

  /** 新規追加を通知 */
  @Output() created = new EventEmitter<void>();

  /** インポートを通知 */
  @Output() imported = new EventEmitter<void>();

  /** 更新を通知 */
  @Output() updated = new EventEmitter<DeviceViewModel>();

  /** 削除を通知 */
  @Output() deleted = new EventEmitter<DeviceViewModel[]>();

  /** ページネーション要素 */
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  /** ソート要素 */
  @ViewChild(MatSort) sort!: MatSort;

  /** カラム */
  public readonly displayedColumns: string[] = [
    'checkbox',
    'serialNo',
    'deviceTypeId',
    'lastCommunicationAt',
    'tenantName',
    'tenantId',
    'contractStartedAt',
    'expiredAt',
    'operation',
  ];

  /** 一覧情報 */
  public dataSource = new MatTableDataSource<DeviceViewModel>([]);

  /** 選択状態 */
  @Input() public selection = new SelectionModel<DeviceViewModel>(true, []);

  /** シリアル番号検索キーワード */
  public serialNoKeyword = '';

  /** デバイス名検索キーワード */
  public deviceNameKeyword = '';

  /** 最終通信日時 */
  public lastCommunicationAtKeyword = '';

  /** テナント名キーワード */
  public tenantNameKeyword = '';

  /** テナントIDキーワード */
  public tenantIdKeyword = '';

  /** 契約開始日キーワード */
  public contractStartedAtKeyword = '';

  /**契約期限 */
  public expiredAtKeyword = '';

  /** 選択されたデバイス種類 */
  public selectedDeviceType: string = '';

  /** 初期ソート値 */
  public initSortState: Sort = {
    active: 'contractStartedAt',
    direction: 'desc',
  };

  /** ソート状態 */
  private sortState: Sort | undefined;

  /** ページ番号 */
  private pageIndex: number | undefined;

  /** 検索フォーム */
  contractFormGroup = new FormGroup({
    contractStartedAt: new FormControl(new Date()),
    contractExpiredAt: new FormControl(subDays(addYears(new Date(), 1), 1)),
  });

  constructor(
    private filterService: FilterService<DeviceListFilter, DeviceViewModel>,
    private cdr: ChangeDetectorRef,
  ) {
    this.filterService.setSessionStorageKey(pageId);
    document.getElementById('device-init-view');
  }

  public ngOnInit(): void {
    // フィルター条件の取得と適用
    this.loadFilters();
  }

  /**
   * 指定した期間が基準を超えないか確認する。
   * @returns
   */
  public checkPeriod() {
    const contractStartedAt = this.contractFormGroup.get('contractStartedAt')
      ?.value as Date;
    const contractExpiredAt = this.contractFormGroup.get('contractExpiredAt')
      ?.value as Date;
    // カレンダーで選択したときは日付は0時0分0秒となるためinitAuditLogList時と合わせるよう個別で値をセットする。
    if (contractExpiredAt) {
      contractExpiredAt.setHours(23);
      contractExpiredAt.setMinutes(59);
      contractExpiredAt.setSeconds(59);
    }

    const isValidPeriod = !isBefore(
      contractStartedAt,
      subDays(contractExpiredAt, MAX_PERIOD),
    );
    return isValidPeriod;
  }

  public ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortingDataAccessor = (item, property) =>
      property === 'deviceTypeId'
        ? item.deviceTypeDisplayName
        : (item as unknown as Record<string, string | number>)[property];
    this.dataSource.sort = this.sort;

    // ソートとページングの変更を検知する
    this.filterService.monitorSortAndPagination(this.sort, this.paginator);

    // ソートとページングの設定
    if (this.sort && this.sortState) {
      const sortState: Sort = {
        active: this.sortState.active,
        direction: this.sortState.direction,
      };

      this.filterService.setSort(this.dataSource, this.sort, sortState);
    }

    // ページングを設定
    if (this.paginator && this.pageIndex) {
      this.setPaginationIndex(this.pageIndex);
    }

    // フィルター設定
    this.dataSource.filterPredicate = (element: DeviceViewModel) => {
      let serialNo = true;
      if (this.serialNoKeyword) {
        // serialNoKeywordが空でない場合のみフィルタリングを行う
        serialNo =
          element.serialNo === undefined
            ? false
            : element.serialNo
                .trim()
                .toLowerCase()
                .indexOf(this.serialNoKeyword.toLowerCase()) > -1;
      }

      let deviceName = true;
      if (this.deviceNameKeyword) {
        // deviceNameKeywordが空でない場合のみフィルタリングを行う
        deviceName =
          element.deviceName === undefined
            ? false
            : element.deviceName
                .trim()
                .toLowerCase()
                .indexOf(this.deviceNameKeyword.toLowerCase()) > -1;
      }

      let deviceTypeDisplayName = true;
      if (this.selectedDeviceType) {
        deviceTypeDisplayName =
          element.deviceTypeDisplayName === this.selectedDeviceType;
      }

      let lastCommunicationAt = true;
      if (this.lastCommunicationAtKeyword) {
        // lastCommunicationAtKeywordが空でない場合のみフィルタリングを行う
        lastCommunicationAt =
          element.lastCommunicationAt === undefined ||
          element.lastCommunicationAt === null
            ? false
            : stringEquals(
                `${new Date(
                  element.lastCommunicationAt,
                ).getFullYear()}/${String(
                  new Date(element.lastCommunicationAt).getMonth() + 1,
                ).padStart(2, '0')}/${String(
                  new Date(element.lastCommunicationAt).getDate(),
                ).padStart(2, '0')}`,
                this.lastCommunicationAtKeyword,
              );
      }

      let tenantName = true;
      if (this.tenantNameKeyword) {
        // tenantNameKeywordが空でない場合のみフィルタリングを行う
        tenantName =
          element.tenantName === undefined
            ? false
            : element.tenantName
                .trim()
                .toLowerCase()
                .indexOf(this.tenantNameKeyword.toLowerCase()) > -1;
      }

      let tenantId = true;
      if (this.tenantIdKeyword) {
        // tenantIdKeywordが空でない場合のみフィルタリングを行う
        tenantId =
          element.tenantId === undefined
            ? false
            : element.tenantId
                .trim()
                .toLowerCase()
                .indexOf(this.tenantIdKeyword.toLowerCase()) > -1;
      }

      let contractStartedAt = true;
      if (this.contractStartedAtKeyword) {
        // expiredAtKeywordが空でない場合のみフィルタリングを行う
        contractStartedAt =
          element.contractStartedAt === undefined
            ? false
            : stringEquals(
                `${element.contractStartedAt.getFullYear()}/${String(
                  element.contractStartedAt.getMonth() + 1,
                ).padStart(2, '0')}/${String(
                  element.contractStartedAt.getDate(),
                ).padStart(2, '0')}`,
                this.contractStartedAtKeyword,
              );
      }

      // expiredAtに対するフィルター機能の実装
      let expiredAt = true;
      if (this.expiredAtKeyword) {
        // expiredAtKeywordが空でない場合のみフィルタリングを行う
        expiredAt =
          element.expiredAt === undefined
            ? false
            : stringEquals(
                `${element.expiredAt.getFullYear()}/${String(
                  element.expiredAt.getMonth() + 1,
                ).padStart(2, '0')}/${String(
                  element.expiredAt.getDate(),
                ).padStart(2, '0')}`,
                this.expiredAtKeyword,
              );
      }

      // フィルター条件を保持
      this.saveFiltersToStorage();

      return (
        serialNo &&
        deviceName &&
        deviceTypeDisplayName &&
        lastCommunicationAt &&
        tenantName &&
        tenantId &&
        contractStartedAt &&
        expiredAt
      );
    };
    this.applyFilter();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['deviceList'] !== undefined) {
      const deviceList: DeviceViewModel[] = changes['deviceList'].currentValue;
      this.dataSource.data = deviceList;
    }
  }

  /**
   * すべての行を選択／非選択に切り替える
   */
  public toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
      return;
    }

    this.selection.select(...this.dataSource.filteredData);
  }

  /**
   *
   */
  public viewChange() {
    const deviceInitView = document.getElementById('device-init-view');
    const deviceAfterView = document.getElementById('device-table-container');
    if (deviceInitView && deviceAfterView) {
      deviceInitView.style.display = 'none';
      deviceAfterView.style.display = 'block';
    }
  }

  /**
   * すべて選択されているか
   *
   * @returns すべて選択されていればtrue, 非選択があればfalse
   */
  public isAllSelected() {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.filteredData.length;
    return numSelected === numRows;
  }

  /**
   * 一括削除する
   */
  public deleteAll() {
    this.deleted.emit(this.selection.selected);
  }

  /**
   * 検索条件（FilterPredicate）に基づいて、フィルタリング処理を適用する。
   */
  public applyFilter(): void {
    // フィルタ処理を発火
    // ※空文字指定だとフィルタリングがされないためdummy文字列を指定している
    // （Angular materialのtable-data-source.tsの_filterDataメソッド参照）
    this.dataSource.filter = 'dummy';
  }

  /**
   * シリアル番号でのフィルタリングをクリアする。
   */
  public clearSerialNoFilter(): void {
    // フィルタ条件をクリア
    this.serialNoKeyword = '';

    this.applyFilter();
  }

  /**
   * 最終更新日時でのフィルタリングをクリアする。
   */
  public clearLastCommunicationAtFilter(): void {
    // フィルタ条件をクリア
    this.lastCommunicationAtKeyword = '';

    this.applyFilter();
  }

  /**
   * テナント名でのフィルタリングをクリアする。
   */
  public clearTenantNameFilter(): void {
    // フィルタ条件をクリア
    this.tenantNameKeyword = '';

    this.applyFilter();
  }

  /**
   * テナントIDでのフィルタリングをクリアする。
   */
  public clearTenantIdFilter(): void {
    // フィルタ条件をクリア
    this.tenantIdKeyword = '';

    this.applyFilter();
  }

  /**
   * 契約開始日でのフィルタリングをクリアする。
   */
  public clearContractStartedAtFilter(): void {
    // フィルタ条件をクリア
    this.contractStartedAtKeyword = '';

    this.applyFilter();
  }

  /**
   * 契約期限でのフィルタリングをクリアする。
   */
  public clearExpiredAtFilter(): void {
    // フィルタ条件をクリア
    this.expiredAtKeyword = '';

    this.applyFilter();
  }

  /**
   * 各フィルターを初期化する。
   */
  public clearFilters() {
    // フィルターを初期化
    this.loadFilters(true);

    const sortState: Sort = this.initSortState;

    this.filterService.setSort(this.dataSource, this.sort, sortState);

    this.setPaginationIndex();

    // フィルターを適用
    this.dataSource.filter = '';
    this.applyFilter();
  }

  /**
   * ページネーションの番号を設定する
   */
  private setPaginationIndex(pageIndex: number = 0) {
    setTimeout(() => {
      this.paginator.pageIndex = pageIndex!;
      this.cdr.detectChanges();
    }, 0);
  }

  /**
   * フィルターをセッションストレージから読み込む。
   */
  private loadFilters(reset?: boolean): void {
    const filters = this.filterService.getFiltersFromSessionStorage(reset);
    this.serialNoKeyword = filters.serialNo || '';
    this.deviceNameKeyword = filters.deviceName || '';
    this.lastCommunicationAtKeyword = filters.lastCommunicationAt || '';
    this.tenantNameKeyword = filters.tenantName || '';
    this.tenantIdKeyword = filters.tenantId || '';
    this.contractStartedAtKeyword = filters.contractStartedAt || '';
    this.expiredAtKeyword = filters.expiredAt || '';
    this.selectedDeviceType = filters.deviceTypeDisplayName || '';
    this.sortState = filters.sortState;
    this.pageIndex = filters.pageIndex;
  }

  /**
   * フィルター条件をセッションストレージに保存する
   */
  private saveFiltersToStorage(): void {
    const filterList: DeviceListFilter = {
      serialNo: this.serialNoKeyword,
      deviceName: this.deviceNameKeyword,
      lastCommunicationAt: this.lastCommunicationAtKeyword,
      tenantName: this.tenantNameKeyword,
      tenantId: this.tenantIdKeyword,
      contractStartedAt: this.contractStartedAtKeyword,
      expiredAt: this.expiredAtKeyword,
      deviceTypeDisplayName: this.selectedDeviceType,
    };
    this.filterService.setFiltersToSessionStorage(filterList);
  }
}
